/**
 * ArtifactCard — clickable file widget shown in chat when the agent produces a file.
 *
 * Two actions:
 *   - Click card → opens the file: the system's default app on native (e.g.
 *     Excel), else a browser preview for renderable types and a save for the
 *     rest — never both (see `openBlob` in `@/api/files`).
 *   - Folder icon → reveals the file in the system file manager (native only)
 *
 * Both go through the shared helpers in `@/api/files` rather than calling the
 * routes here. This card used to POST `/open` itself and `window.open` the
 * response's `fallback_url` — which points at `/api/files/serve?path=`, RETIRED
 * in Phase D (#44), so in the cloud web app clicking a file card opened a tab
 * showing a 404 body. The panel's copy of this card had already moved to the
 * helpers; this one hadn't.
 */

import { useState } from "react";
import { isNativeHost } from "@/api/client";
import { openArtifactSmart, revealArtifact } from "@/api/files";
import { BUBBLE_WIDTH } from "./MessageBubble";

interface Props {
  artifactId: string;
  filename: string;
  fileType: string;
}

const FILE_ICONS: Record<string, string> = {
  xlsx: "📊",
  csv: "📄",
  json: "📋",
  pdf: "📕",
};

export function ArtifactCard({ artifactId, filename, fileType }: Props) {
  const [busy, setBusy] = useState(false);

  const handleOpen = async () => {
    setBusy(true);
    try {
      await openArtifactSmart(artifactId, filename);
    } catch (e) {
      console.error("Open artifact failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const handleReveal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await revealArtifact(artifactId);
    } catch (e) {
      console.error("Reveal artifact failed:", e);
    }
  };

  const icon = FILE_ICONS[fileType] ?? "📎";
  const ext = fileType.toUpperCase();
  // "Show in folder" needs a real desktop file manager (native shell only). In a
  // browser (cloud web) hide it; the card's Open already serves/downloads the file.
  const showReveal = isNativeHost();

  return (
    <div className="flex justify-start animate-[fadeInUp_200ms_ease-out]">
      <div className={`group flex items-center gap-3 rounded-2xl border border-jove-60/30
                      bg-gradient-to-r from-jove-10 to-surface shadow-sm ${BUBBLE_WIDTH}`}>
        {/* Main clickable area — opens the file */}
        <button
          onClick={handleOpen}
          disabled={busy}
          className={`flex items-center gap-3 px-4 py-3 ${showReveal ? "rounded-l-2xl" : "rounded-2xl"}
                     hover:bg-jove-30/30 active:scale-[0.99] transition-all duration-150
                     disabled:opacity-60 disabled:cursor-wait flex-1 min-w-0`}
        >
          {/* File icon */}
          <div className="w-10 h-10 rounded-xl bg-jove-30 flex items-center justify-center shrink-0
                          group-hover:bg-jove-50 transition-colors">
            <span className="text-lg">{icon}</span>
          </div>

          {/* File info */}
          <div className="text-left min-w-0">
            <p className="text-sm font-medium text-space truncate">{filename}</p>
            <p className="text-[10px] text-space-40 mt-0.5">
              {ext} file {busy ? "— opening..." : "— click to open"}
            </p>
          </div>

          {/* Open icon */}
          <div className="ml-2 shrink-0 text-jove-80 group-hover:text-jove transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </div>
        </button>

        {/* Reveal in folder button — native desktop only. */}
        {showReveal && (
          <button
            onClick={handleReveal}
            className="px-3 py-3 border-l border-jove-60/20 text-space-40
                       hover:text-jove hover:bg-jove-30/30 transition-colors rounded-r-2xl shrink-0"
            title="Show in folder"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
